import { Employee, RolesEnum } from "@finway-group/shared/lib/models"
import React, { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { INTERCOM_APP_ID, INTERCOM_TEST_APP_ID, isDemo } from "Shared/config/consts"
import { useLoggedInEmployeeProfile, useLoggedInUserProfile } from "Shared/hooks/user.hooks"
import Breakpoints from "Shared/utils/breakpoints"
import { isDev, isInternal } from "Shared/utils/helpers"
import { useResizeWindowWidth } from "Shared/utils/hooks/useResizeWindowWidth"

const extractIntercomUserData = (user?: Employee, tenantId?: string, currentPage?: string) => {
    if (!user) return {}

    return {
        user_id: user.id,
        name: user.getFullName(),
        email: user.email,
        finway_role: RolesEnum[user.activeCompanyProfile?.role], // custom attribute
        finway_tenant_id: tenantId, // custom attribute
        finway_current_page: currentPage, // custom attribute
        ...(user.img && {
            avatar: {
                type: "avatar",
                image_url: user.img,
            },
        }),
    }
}

const intercomCodeSnippet = (APP_ID: string = "") => {
    ;(function () {
        var w = window
        var ic = (w as any).Intercom
        if (typeof ic === "function") {
            ic("reattach_activator")
            ic("update", (w as any).intercomSettings)
        } else {
            var d = document
            var i = function () {
                ;(i as any).c(arguments)
            }
            ;(i as any).q = []
            ;(i as any).c = function (args: any) {
                ;(i as any).q.push(args)
            }
            ;(w as any).Intercom = i
            var l = function () {
                var s = d.createElement("script")
                s.type = "text/javascript"
                s.async = true
                s.src = "https://widget.intercom.io/widget/" + APP_ID
                var x = d.getElementsByTagName("script")[0]
                x.parentNode?.insertBefore(s, x)
            }
            if (document.readyState === "complete") {
                l()
            } else if ((w as any).attachEvent) {
                ;(w as any).attachEvent("onload", l)
            } else {
                w.addEventListener("load", l, false)
            }
        }
    })()
}

const Intercom: React.FC = () => {
    const loggedInUser = useLoggedInEmployeeProfile()
    const loggedInUserId = loggedInUser.id
    const tenantId = useLoggedInUserProfile()?.tenantId
    const location = useLocation()
    const windowWidth = useResizeWindowWidth()
    const [hasBooted, setHasBooted] = useState(false)
    const APP_ID = isDev || isInternal ? INTERCOM_TEST_APP_ID : INTERCOM_APP_ID

    // when the user ID changes (different logins or logouts), do the corresponding action
    useEffect(() => {
        if (!(window as any).Intercom) intercomCodeSnippet(APP_ID)

        if (!loggedInUserId && hasBooted) shutdownIntercom()
        else if (loggedInUserId && !hasBooted) bootIntercom()
    }, [loggedInUserId])

    // intercom should be updated on every URL change
    useEffect(() => {
        if (loggedInUserId && hasBooted) updateIntercom()
    }, [location.pathname])

    // when user data that is used for intercom changes, update as well
    useEffect(() => {
        if (loggedInUserId && hasBooted) updateIntercom()
    }, [loggedInUser.lastName, loggedInUser.firstName, loggedInUser.activeCompanyProfile?.role, loggedInUser.email, loggedInUser.img])

    // intercom launcher should be slightly moved up when expense action buttons are at the bottom of the page.
    useEffect(() => {
        ;(window as any).Intercom("update", {
            vertical_padding: location.pathname.includes("/expenses/") && windowWidth < Breakpoints.sm ? 50 : 20,
        })
    }, [location.pathname, windowWidth])

    const bootIntercom = () => {
        ;(window as any).Intercom("boot", {
            app_id: APP_ID,
            ...extractIntercomUserData(loggedInUser, tenantId, location.pathname),
        })
        setHasBooted(true)
    }

    const updateIntercom = () => {
        ;(window as any).Intercom("update", {
            app_id: APP_ID,
            ...extractIntercomUserData(loggedInUser, tenantId, location.pathname),
        })
    }

    const shutdownIntercom = () => {
        ;(window as any).Intercom("shutdown")
        setHasBooted(false)
    }

    return <></>
}

export default Intercom
